import { FunctionComponent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Col, Flex, Row } from 'antd';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

// components
import BackButton from 'components/backButton/backButton';
import DisabledElement from 'components/disabledElement/disabledElement';
import StatusComponent from 'components/status/status';
import PageTitle from 'components/title/title';

// services
import { onGetRegistryTransactionById } from '../services/registryTransactionServices';

// models
import { RegistryTransaction } from '../models/registryTransaction';
import NotificationService from 'core/services/notification';
import LoadingComponent from 'components/loadingComponent/loadingComponent';
import { StatusColors } from 'utils/statusColors';
import { isRTL } from './../../../../../core/services/language';

interface RegistryTransactionViewPageProps {}

const RegistryTransactionViewPage: FunctionComponent<RegistryTransactionViewPageProps> = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();

	const [isLoading, setIsLoading] = useState(true);
	const [registryTransaction, setRegistryTransaction] = useState<RegistryTransaction>();

	useEffect(() => {
		getRegistryTransaction();
	}, []);

	const getRegistryTransaction = async () => {
		if (id === undefined) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			navigate('/dashboard/actions/registry-transactions');
		}

		setIsLoading(true);

		const data = await onGetRegistryTransactionById(id as string);
		setRegistryTransaction(data);

		setIsLoading(false);
		try {
		} catch (error) {
			navigate('/dashboard/actions/registry-transactions');
		}
	};

	return (
		<>
			<PageTitle name={t('TITLE.ACTIONS')} />

			<BackButton styles={{ paddingTop: 16 }} />

			{isLoading ? (
				<LoadingComponent />
			) : (
				<Card>
					<Row gutter={[16, 16]}>
						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.SELLER_NATIONAL_ID')} value={registryTransaction?.source.national_id} />
						</Col>

						<Col span={24} md={12}>
							<DisabledElement label={t('ACTIONS.BUYER_NATIONAL_ID')} value={registryTransaction?.destination.national_id} />
						</Col>

						<Col span={24}>
							{registryTransaction?.transactions.map((transaction, index) => (
								<Card key={index} className="mb-4">
									<Flex align="center" gap={20} className="mb-3">
										<h6 className="mb-0">
											{transaction.share_type === 'Common' ? t('COMMON.COMMON_SHARES') : t('COMMON.PREFERRED_SHARES')} #{transaction?.id}
										</h6>
										<StatusComponent text={t(`COMMON.${transaction.status.toUpperCase()}`)} small color={StatusColors[transaction.status]} />
									</Flex>

									<StatusComponent color="white" bordered text={`${t('COMMON.NUM_OF_SHARES')}: ${transaction.number_of_shares}`} />

									<Row gutter={[16, 16]} className="mt-4">
										<Col span={24} md={12}>
											<Card className="h-100">
												<Flex justify="space-between" align="center" gap={10}>
													<div>
														<h6 className="mb-0 text-primary">
															{isRTL() ? transaction?.source.name : transaction?.source.name_en ?? transaction?.source.name}
														</h6>
														<span className="text-light">{transaction.seller_accepted_at}</span>
													</div>
													<StatusComponent
														text={transaction.seller_accepted_at ? t('ACTIONS.ACCEPTED') : t('COMMON.PENDING')}
														color={transaction.seller_accepted_at ? 'green' : 'orange'}
													/>
												</Flex>
											</Card>
										</Col>

										<Col span={24} md={12}>
											<Card className="h-100">
												<Flex justify="space-between" align="center" gap={10}>
													<div>
														<h6 className="mb-0 text-primary">
															{isRTL() ? transaction?.destination.name : transaction?.destination.name_en ?? transaction?.source.name}
														</h6>
														<span className="text-light">{transaction.buyer_accepted_at}</span>
													</div>
													<StatusComponent
														text={transaction.buyer_accepted_at ? t('ACTIONS.ACCEPTED') : t('COMMON.PENDING')}
														color={transaction.buyer_accepted_at ? 'green' : 'orange'}
													/>
												</Flex>
											</Card>
										</Col>
									</Row>
								</Card>
							))}
						</Col>
					</Row>
				</Card>
			)}
		</>
	);
};

export default RegistryTransactionViewPage;
